"use client";

import { getYear } from "date-fns";
import { createContext, useContext, useMemo, PropsWithChildren } from "react";
import { TopLevelEvent } from "@/components/headToHead/types";
import { SPORTSBOOK_URL } from "@/constants/amplitude";
import {
  BasicTeam,
  Maybe,
  EventStatus,
  GetHeadToHeadTopLevelDataQuery,
} from "@/graphql/gql/graphql";
import { usePrefetchHeadToHeadPlayerStats } from "./useGetHeadToHeadPlayerStats";
import { usePrefetchHeadToHeadTeamStats } from "./useGetHeadToHeadTeamStats";

export interface HeadToHeadContextType {
  eventName: string;
  awayTeam: Maybe<BasicTeam> | undefined;
  homeTeam: Maybe<BasicTeam> | undefined;
  scoreboard: TopLevelEvent["scoreboard"];
  numberfireId: TopLevelEvent["numberfireId"];
  season: TopLevelEvent["season"];
  week: TopLevelEvent["week"];
  venueName: TopLevelEvent["venueName"];
  venueType: TopLevelEvent["venueType"];
  startTime: TopLevelEvent["startTime"];
  status: TopLevelEvent["status"];
  broadcastInfo: TopLevelEvent["broadcastInfo"];
  gameId: number;
  seasonYear: number;
  gameState: EventStatus;
  eventUrl: string;
}

const HeadToHeadContext = createContext<HeadToHeadContextType>({
  eventName: "", // For "Event Name" Amplitude tracking prop
  awayTeam: null,
  homeTeam: null,
  scoreboard: null,
  numberfireId: null,
  season: null,
  week: null,
  venueName: null,
  venueType: null,
  startTime: null,
  status: null,
  broadcastInfo: null,
  gameId: 0,
  seasonYear: getYear(new Date()),
  gameState: EventStatus.Scheduled,
  eventUrl: SPORTSBOOK_URL,
});

export function PrefetchPreGame({ gameId }: { gameId: number }) {
  usePrefetchHeadToHeadPlayerStats(gameId);
  usePrefetchHeadToHeadTeamStats(gameId);

  return null;
}

export function HeadToHeadContextProvider({
  children,
  gameId,
  gameState,
  minimumViableData,
}: PropsWithChildren<{
  gameId: number;
  minimumViableData: GetHeadToHeadTopLevelDataQuery;
  gameState: EventStatus | null | undefined;
}>) {
  const event = minimumViableData?.event;
  const awayTeam = event?.awayTeam;
  const homeTeam = event?.homeTeam;
  const scoreboard = event?.scoreboard;
  const numberfireId = event?.numberfireId;
  const season = event?.season;
  const week = event?.week;
  const venueName = event?.venueName;
  const venueType = event?.venueType;
  const startTime = event?.startTime;
  const status = event?.status;
  const broadcastInfo = event?.broadcastInfo;
  const eventName = `${awayTeam?.name} @ ${homeTeam?.name}`
    .replace(/ /g, "-")
    .toLowerCase();
  const seasonYear = event?.season ?? getYear(new Date());
  const eventUrl = event?.eventUrl ?? SPORTSBOOK_URL;
  const value = useMemo(
    () => ({
      awayTeam,
      homeTeam,
      scoreboard,
      numberfireId,
      season,
      week,
      venueName,
      venueType,
      startTime,
      status,
      broadcastInfo,
      eventName,
      gameId,
      seasonYear,
      gameState: gameState ?? EventStatus.Scheduled,
      eventUrl,
    }),
    [
      awayTeam,
      homeTeam,
      scoreboard,
      numberfireId,
      season,
      week,
      venueName,
      venueType,
      startTime,
      status,
      broadcastInfo,
      eventName,
      gameId,
      seasonYear,
      gameState,
      eventUrl,
    ]
  );

  return (
    <>
      {gameState === EventStatus.Scheduled && (
        <PrefetchPreGame gameId={gameId} />
      )}
      <HeadToHeadContext value={value}>{children}</HeadToHeadContext>
    </>
  );
}

export const useHeadToHeadContext = () => {
  const data = useContext(HeadToHeadContext);
  return data;
};
